import { yupResolver } from '@hookform/resolvers/yup'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import SaveAsIcon from '@mui/icons-material/SaveAs'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useEffect, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import axiosClient from 'utils/axiosClient'
import * as yup from 'yup'
import CancelIcon from '@mui/icons-material/Cancel'

type Props = {
  apiEndpoint?: string
  label: string
  lastName: string
  firstName: string
}
interface FormInput {
  lastName: string
  firstName: string
}

export default function EditName({
  apiEndpoint,
  label,
  lastName,
  firstName,
}: Props) {
  const { enqueueSnackbar } = useSnackbar()
  const schema = yup.object({
    lastName: yup.string().required(),
    firstName: yup.string().required(),
  })
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      lastName: lastName,
      firstName: firstName,
    },
  })
  const [loading, setLoading] = useState(false)
  const [edit, setEdit] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const handleKeyDown = (e: globalThis.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setEdit(false)
      setValue('lastName', lastName, {
        shouldValidate: true,
      })
      setValue('firstName', firstName, {
        shouldValidate: true,
      })
    }
  }
  useEffect(() => {
    if (ref.current) {
      if (edit) {
        ref.current.addEventListener('keydown', (e) => {
          handleKeyDown(e)
        })
      } else {
        ref.current.removeEventListener('keydown', (e) => {
          handleKeyDown(e)
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit])
  const onSubmit: SubmitHandler<FormInput> = async (data) => {
    if (apiEndpoint) {
      setLoading(true)
      try {
        await axiosClient.put(apiEndpoint, {
          data: JSON.stringify(data),
        })
        setEdit(false)
        const msg = label + 'を修正しました。'
        enqueueSnackbar(msg, {
          variant: 'success',
        })
        setLoading(false)
      } catch (_error) {
        const msg = 'エラーが発生しました。'
        enqueueSnackbar(msg, {
          variant: 'error',
        })
      }
    }
  }
  return (
    <Box
      component="form"
      noValidate
      autoComplete="off"
      onSubmit={handleSubmit(onSubmit)}
      my={2}
    >
      {edit ? (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          alignItems="center"
          sx={{ display: edit ? 'flex' : 'none' }}
          ref={ref}
        >
          <TextField
            variant={'standard'}
            // required
            margin="dense"
            label={'姓'}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEdit(false)
                setValue('lastName', lastName, {
                  shouldValidate: true,
                })
              }
            }}
            {...register('lastName')}
            error={'lastName' in errors}
            helperText={errors.lastName?.message}
          />
          <TextField
            sx={{
              ml: 2,
            }}
            variant={'standard'}
            required
            margin="dense"
            label={'名'}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setEdit(false)
                setValue('firstName', firstName, {
                  shouldValidate: true,
                })
              }
            }}
            {...register('firstName')}
            error={'firstName' in errors}
            helperText={errors.firstName?.message}
          />
          <Stack direction="row">
            <Button
              sx={{
                mx: 1,
                px: 1,
                minWidth: 0,
              }}
              size="small"
              color="error"
              variant="contained"
              onClick={() => {
                setEdit(false)
              }}
            >
              <CancelIcon />
            </Button>
            <LoadingButton
              disabled={Object.keys(errors).length > 0}
              loading={loading}
              sx={{
                mx: 1,
                px: 1,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              type="submit"
            >
              <SaveAsIcon />
            </LoadingButton>
          </Stack>
        </Stack>
      ) : (
        <Stack
          direction={{ md: 'row', xs: 'column' }}
          spacing={2}
          sx={{ display: !edit ? 'flex' : 'none' }}
          alignItems={{
            md: 'center',
            xs: 'flex-start',
          }}
        >
          <Typography fontWeight="bold" variant="body2" flex={'none'}>
            {label}
          </Typography>
          <Stack direction="row" alignItems="center">
            <Typography>{`${lastName} ${firstName}`}</Typography>
            <Button
              sx={{
                flex: 'none',
                mx: 1,
                p: 0.5,
                minWidth: 0,
              }}
              size="small"
              variant="contained"
              color="info"
              onClick={() => {
                setEdit(true)
              }}
            >
              <ModeEditIcon fontSize="small" />
            </Button>
          </Stack>
        </Stack>
      )}
    </Box>
  )
}
